import { FileUpload } from '@mui/icons-material';
import { Box, Button, TextField } from '@mui/material';
import { ENavigationButton } from 'components/Buttons/NavigationButtons';
import { ChangeEvent, useEffect, useState } from 'react';
import { PUBMED_MAX_IDS_PER_REQUEST } from 'hooks/external/useFetchPubMedIds.types';
import CurationImportStyles from '../CurationImport.styles';

enum EValidationReason {
    EMPTY = 'PubMed ID input is empty',
    INCORRECT = 'PubMed ID format is incorrect or unsupported',
    TOO_BIG = 'Please limit uploads to 10,000 PMIDs at a time',
}

const isValidNumberList = (rawIdText: string | undefined) => {
    if (!rawIdText) return false;
    return rawIdText
        .trim()
        .split(/[\r?\n]+/)
        .every((pmid) => /^\d+$/.test(pmid));
};

const ImportPMIDsUpload = (props: {
    onPubmedIdsUploaded: (parsedIds: string[]) => void;
    onNavigate: (button: ENavigationButton) => void;
    onFileUpload: (fileName: string) => void;
}) => {
    const [uploadState, setUploadState] = useState<{
        parsedIdList: string[];
        rawIdText: string;
        file: File | undefined;
        isValid: boolean;
        validationReason: EValidationReason | null;
    }>({
        parsedIdList: [],
        rawIdText: '',
        file: undefined,
        isValid: false,
        validationReason: EValidationReason.EMPTY,
    });

    useEffect(() => {
        if (uploadState.rawIdText.length === 0) {
            setUploadState((prev) => ({
                ...prev,
                isValid: false,
                validationReason: EValidationReason.EMPTY,
            }));
            return;
        }

        const isValid = isValidNumberList(uploadState.rawIdText);
        if (!isValid) {
            setUploadState((prev) => ({
                ...prev,
                isValid: false,
                validationReason: EValidationReason.INCORRECT,
            }));
            return;
        }
        const textIdsToStringArr = uploadState.rawIdText
            .split(/[\r?\n]+/)
            .map((x) => x.trim())
            .filter((x) => !!x);
        if (textIdsToStringArr.length > PUBMED_MAX_IDS_PER_REQUEST) {
            setUploadState((prev) => ({
                ...prev,
                isValid: false,
                validationReason: EValidationReason.TOO_BIG,
            }));
            return;
        }

        setUploadState((prev) => ({
            ...prev,
            parsedIdList: textIdsToStringArr,
            isValid: true,
            validationReason: null,
        }));
    }, [uploadState.rawIdText]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.target?.files && event.target.files[0]) {
            const file = event.target.files[0];
            props.onFileUpload(file.name);
            setUploadState((prev) => ({
                ...prev,
                file: file,
            }));

            const reader = new FileReader();
            reader.onload = function () {
                const content = reader.result;
                if (content && typeof content === 'string') {
                    // we have trouble testing string patterns for carriage returns like \r\n, so we replace them here with new \n newlines
                    const replacedText = content.replace(/(?:[\r\n])+/g, '\n');
                    setUploadState((prev) => ({
                        ...prev,
                        rawIdText: replacedText,
                    }));
                }
            };
            reader.readAsText(file);
        }
    };

    const handleInputIds = (event: ChangeEvent<HTMLInputElement>) => {
        setUploadState((prev) => ({
            ...prev,
            rawIdText: event.target.value,
        }));
    };

    const handleButtonClick = (button: ENavigationButton) => {
        if (button === ENavigationButton.PREV) {
            props.onNavigate(button);
        } else {
            props.onPubmedIdsUploaded(uploadState.parsedIdList);
        }
    };

    return (
        <Box sx={CurationImportStyles.importContainer}>
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <Button component="label" endIcon={<FileUpload />}>
                    {uploadState.file?.name || 'Upload File'}
                    <input onChange={handleFileUpload} type="file" hidden />
                </Button>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '0.5rem',
                }}
            >
                or
            </Box>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <TextField
                        value={uploadState.rawIdText}
                        onChange={handleInputIds}
                        rows={10}
                        multiline
                        placeholder="Enter list of pubmed IDs separated by a newline"
                        helperText={uploadState.validationReason || ''}
                        error={!uploadState.isValid}
                        sx={{ width: '400px' }}
                    />
                </Box>
            </Box>
            <Box sx={CurationImportStyles.actionsContainer}>
                <Button
                    variant="outlined"
                    color="error"
                    sx={CurationImportStyles.actionsButton}
                    onClick={() => handleButtonClick(ENavigationButton.PREV)}
                >
                    cancel
                </Button>
                <Button
                    variant="contained"
                    sx={CurationImportStyles.actionsButton}
                    disableElevation
                    disabled={uploadState.parsedIdList.length === 0 || uploadState.isValid === false}
                    onClick={() => handleButtonClick(ENavigationButton.NEXT)}
                >
                    next
                </Button>
            </Box>
        </Box>
    );
};

export default ImportPMIDsUpload;
